/**
 * Clearance-distance rules: table-to-table, dance floor buffer, DJ booth
 * buffer, feature zone crowding. Each maps directly to a row in PRD §8.2.
 */

import type { LayoutObject, ObjectCatalogEntry, ValidationIssue } from '../../types';
import { distanceBetween, effectiveRadius } from './geometry';
import { CLEARANCE } from './clearance';

const SEATING_TYPES = new Set(['round_table', 'rect_table_6', 'rect_table_8', 'head_table', 'sweetheart_table']);
const FEATURE_ZONE_TYPES = new Set(['cake_table', 'gift_table', 'dessert_station', 'donut_wall', 'photo_booth']);
const DANCE_FLOOR_TYPES = new Set(['dance_floor_15', 'dance_floor_18']);

export function checkTableToTableClearance(
  objects: LayoutObject[],
  catalog: ObjectCatalogEntry[]
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const seatingObjects = objects.filter((o) => SEATING_TYPES.has(o.type));

  for (let i = 0; i < seatingObjects.length; i++) {
    for (let j = i + 1; j < seatingObjects.length; j++) {
      const a = seatingObjects[i];
      const b = seatingObjects[j];
      const distance = distanceBetween(a, b);
      const footprintTouchDistance = effectiveRadius(a, catalog) + effectiveRadius(b, catalog);
      const recommendedMinDistance = footprintTouchDistance + CLEARANCE.ROUND_TABLE_TO_ROUND_TABLE_FT;

      if (distance < footprintTouchDistance) {
        // The tables' own footprints genuinely intersect — this is a
        // real, physical impossibility (furniture can't actually
        // occupy the same space), not a matter of taste about ideal
        // spacing. This is the brief's explicit "Objects overlapping"
        // Critical example.
        issues.push({
          id: `clearance-${a.id}-${b.id}`,
          severity: 'critical',
          message: `${a.label} and ${b.label} are overlapping.`,
          relatedObjectIds: [a.id, b.id],
        });
      } else if (distance < recommendedMinDistance) {
        // Footprints don't overlap — there's real, if tight, space
        // between them. This is extremely common in real wedding
        // layouts and should read as a gentle recommendation, not an
        // alarm: per direct coordinator feedback, treating this the
        // same as a true overlap is exactly what trained coordinators
        // to start ignoring every warning.
        issues.push({
          id: `clearance-${a.id}-${b.id}`,
          severity: 'info',
          message: `${a.label} and ${b.label} are closer than the recommended 18 inches \u2014 still workable, just tight.`,
          relatedObjectIds: [a.id, b.id],
        });
      }
    }
  }

  return issues;
}

export function checkDanceFloorBuffer(
  objects: LayoutObject[],
  catalog: ObjectCatalogEntry[]
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const danceFloors = objects.filter((o) => DANCE_FLOOR_TYPES.has(o.type));
  const seatingObjects = objects.filter((o) => SEATING_TYPES.has(o.type));

  for (const floor of danceFloors) {
    for (const table of seatingObjects) {
      const distance = distanceBetween(floor, table);
      const footprintTouchDistance = effectiveRadius(floor, catalog) + effectiveRadius(table, catalog);
      const recommendedMinDistance = footprintTouchDistance + CLEARANCE.DANCE_FLOOR_BUFFER_FT;

      if (distance < footprintTouchDistance) {
        // The brief's explicit Critical example: "Dance floor
        // overlapping furniture." A real physical conflict, not a
        // spacing preference.
        issues.push({
          id: `dancefloor-buffer-${floor.id}-${table.id}`,
          severity: 'critical',
          message: `${table.label} is overlapping the dance floor.`,
          relatedObjectIds: [floor.id, table.id],
        });
      } else if (distance < recommendedMinDistance) {
        // Close enough that a chair might get bumped during dancing —
        // worth knowing, not worth alarm. Matches the brief's Warning-
        // tier "significant chair overlap" example more closely than
        // Critical, since the footprints themselves are still clear.
        issues.push({
          id: `dancefloor-buffer-${floor.id}-${table.id}`,
          severity: 'warning',
          message: `${table.label} is very close to the dance floor \u2014 guests may bump chairs.`,
          relatedObjectIds: [floor.id, table.id],
        });
      }
    }
  }

  return issues;
}

export function checkDjBoothBuffer(
  objects: LayoutObject[],
  catalog: ObjectCatalogEntry[]
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const djBooths = objects.filter((o) => o.type === 'dj_booth');
  const others = objects.filter((o) => o.type !== 'dj_booth');

  for (const dj of djBooths) {
    for (const other of others) {
      const distance = distanceBetween(dj, other);
      const minDistance = effectiveRadius(dj, catalog) + effectiveRadius(other, catalog) + CLEARANCE.DJ_BOOTH_BUFFER_FT;
      if (distance < minDistance) {
        // Critical, not a graduated severity like the spacing checks
        // above — the brief lists "DJ setup blocking access" directly
        // among its Critical examples. Unlike a table-to-table spacing
        // preference, an inaccessible DJ booth is itself the problem
        // this rule exists to catch, not a milder version of one.
        issues.push({
          id: `dj-buffer-${dj.id}-${other.id}`,
          severity: 'critical',
          message: `${other.label} is blocking DJ access.`,
          relatedObjectIds: [dj.id, other.id],
        });
      }
    }
  }

  return issues;
}

export function checkFeatureZoneCrowding(
  objects: LayoutObject[],
  catalog: ObjectCatalogEntry[]
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const featureZones = objects.filter((o) => FEATURE_ZONE_TYPES.has(o.type));

  for (let i = 0; i < featureZones.length; i++) {
    for (let j = i + 1; j < featureZones.length; j++) {
      const a = featureZones[i];
      const b = featureZones[j];
      const distance = distanceBetween(a, b);
      const minDistance = effectiveRadius(a, catalog) + effectiveRadius(b, catalog) + CLEARANCE.FEATURE_ZONE_BUFFER_FT;
      if (distance < minDistance) {
        // Warning, not Informational — per the brief's own "objects
        // becoming difficult to access" example, two feature zones
        // crowded together (e.g. the cake table jammed against the
        // gift table) genuinely can make both harder to serve from or
        // approach, not merely a cosmetic spacing preference.
        issues.push({
          id: `feature-crowding-${a.id}-${b.id}`,
          severity: 'warning',
          message: `${a.label} and ${b.label} are very close together \u2014 may be hard to access.`,
          relatedObjectIds: [a.id, b.id],
        });
      }
    }
  }

  return issues;
}
